import { useEffect, useRef } from 'react'
import { WifiOff } from 'lucide-react'
import { useOnline } from '../responsive/useOnline'
import { emit } from '../analytics/analytics'
import './offlineBanner.css'

/**
 * The app-wide "you're offline" strip — issue #274, decision 1.
 *
 * **A banner, not a takeover — `useOnline()`'s first consumer.** The hook has
 * been built and tested since #266 and nothing read it; `App.tsx`'s
 * `OFFLINE_PATH` docblock records why swapping the whole app for the routed
 * `Offline` screen the moment `navigator.onLine` goes false was rejected: that
 * screen's own copy promises "what's already loaded in this tab is still here
 * to read", and a full-page takeover is precisely what would make that false.
 * TanStack Query keeps serving its in-memory cache regardless of connectivity,
 * so the honest answer is a strip that says the connection is down while every
 * page underneath keeps rendering. `routes/errors/Offline.tsx` still exists,
 * still routed at `/offline`, and is not touched by this component — it is a
 * second, independent way to reach the same fact, not replaced by it.
 *
 * **`role="status"` with `aria-live="polite"`, not `role="alert"`.**
 * `ToastProvider` draws the same line for the same reason: an assertive region
 * interrupts whatever a screen reader is announcing to say so immediately,
 * which is right for a toast reporting a failed save and wrong for a strip
 * that is about to sit on screen for as long as the connection stays down —
 * the content behind it is unaffected, nothing here is an emergency, and a
 * `polite` announcement still reaches an AT user without cutting off whatever
 * else they were being told. There is no focusable control in this banner, so
 * nothing about it can steal focus either way.
 *
 * **No dismiss control.** `VerifyEmailBanner` is dismissible because it
 * reminds the user of a fact that stays true whether or not they are told
 * again; this banner reports a LIVE fact, and hiding it while still offline
 * would make the chrome lie about the connection. It clears itself the moment
 * `useOnline()` reports `true` — there is nothing for a user to dismiss.
 *
 * **Plain document flow, not `position: fixed`.** Mounted above
 * `<BrowserRouter>` in `main.tsx` — the same slot `AppTexture` occupies — so it
 * is not inside any route's own scroll container. Taking up real height when it
 * appears pushes the chrome below it down by exactly that height, which keeps
 * it visible without needing a z-index above the sticky top bar (`shell.css`'s
 * `.topbar` is `z-index: 40`) or fighting that bar's own stacking context.
 *
 * **`navigator.onLine === false` under-reports, never over-reports** — see
 * `useOnline.ts`'s own docblock. That is a property of the hook this component
 * inherits rather than one it can correct: a captive portal or a dead uplink
 * with the network interface still up will not show this banner, but nothing
 * here will show it spuriously either.
 */
export function OfflineBanner() {
  const online = useOnline()

  /**
   * `offline_shown` fires once per transition into being offline, not once per
   * render — the same ref-guard shape `MfaChallenge.tsx` uses for
   * `mfa_challenge_shown`. `useOnline` only changes value on a real
   * online/offline event, so in practice this already fires once per
   * transition either way; the guard is what keeps that true under
   * `StrictMode`'s double-invoked effects in dev, rather than relying on it.
   */
  const announced = useRef(false)
  useEffect(() => {
    if (!online) {
      if (!announced.current) {
        announced.current = true
        emit('offline_shown')
      }
    } else {
      announced.current = false
    }
  }, [online])

  if (online) return null

  return (
    <div className="offline-banner" role="status" aria-live="polite">
      <WifiOff className="offline-banner__icon" size={16} strokeWidth={2} aria-hidden="true" />
      <p className="offline-banner__text">
        You&apos;re offline. What&apos;s already loaded in this tab is still here to read — new
        data needs the connection back.
      </p>
    </div>
  )
}
